import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Layers, Plus, Edit, Trash2, X, Download, Eye, Image as ImageIcon, Search, FileDown, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { cadOrderAPI, customerAPI } from '@/lib/api';
import * as XLSX from 'xlsx';
import { useAuth } from '@/context/AuthContext';

const CADOrders = () => {
  const { canAdd, canEdit, canDelete, hasModuleAccess } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fullScreenImage, setFullScreenImage] = useState(null);
  const [fullScreenImages, setFullScreenImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  
  const [formData, setFormData] = useState({
    party_name: '',
    bag_no: '',
    order_images: [],
    cad_images: [],
    stone_details_images: [],
    remarks: '',
    confirmation_remark: '',
    status: 'On Process'
  });

  useEffect(() => {
    loadData();
  }, []);

  // Check if user has access to this module
  useEffect(() => {
    if (!hasModuleAccess('cad')) {
      toast.error('You do not have access to this module');
      window.location.href = '/';
      return;
    }
  }, [hasModuleAccess]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [ordersData, customersData] = await Promise.all([
        cadOrderAPI.getAll(),
        customerAPI.getAll()
      ]);
      setOrders(ordersData);
      setCustomers(customersData);
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      party_name: '',
      bag_no: '',
      order_images: [],
      cad_images: [],
      stone_details_images: [],
      remarks: '',
      confirmation_remark: '',
      status: 'Pending'
    });
  };

  const handleImageUpload = (e, imageType) => {
    const files = Array.from(e.target.files);
    const maxImages = 3;
    
    if (formData[imageType].length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed for ${imageType.replace(/_/g, ' ')}`);
      return;
    }

    const validFiles = [];
    
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per image.`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }

      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [imageType]: [...(prev[imageType] || []), reader.result]
        }));
      };
      reader.onerror = () => {
        toast.error(`Failed to read ${file.name}`);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (imageType, index) => {
    setFormData(prev => ({
      ...prev,
      [imageType]: prev[imageType].filter((_, i) => i !== index)
    }));
  };

  const handleAddOrder = async () => {
    try {
      // Clean up formData to ensure arrays are properly formatted
      const orderData = {
        party_name: formData.party_name || null,
        bag_no: formData.bag_no || null,
        order_images: Array.isArray(formData.order_images) ? formData.order_images : [],
        cad_images: Array.isArray(formData.cad_images) ? formData.cad_images : [],
        stone_details_images: Array.isArray(formData.stone_details_images) ? formData.stone_details_images : [],
        remarks: formData.remarks || null,
        confirmation_remark: formData.confirmation_remark || null,
        status: formData.status || 'On Process'
      };
      
      console.log('Creating order with data:', orderData);
      const newOrder = await cadOrderAPI.create(orderData);
      setOrders([...orders, newOrder]);
      resetForm();
      setIsAddDialogOpen(false);
      toast.success('CAD Order created successfully');
    } catch (error) {
      const errorMsg = error.message || 'Failed to create order';
      toast.error(errorMsg);
      console.error('Error creating order:', error);
    }
  };

  const handleEditOrder = async () => {
    try {
      const updated = await cadOrderAPI.update(editingOrder.id, formData);
      setOrders(orders.map(o => o.id === editingOrder.id ? updated : o));
      setIsEditDialogOpen(false);
      setEditingOrder(null);
      resetForm();
      toast.success('Order updated successfully');
    } catch (error) {
      toast.error('Failed to update order');
      console.error('Error updating order:', error);
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm('Are you sure you want to delete this CAD order?')) return;

    try {
      await cadOrderAPI.delete(id);
      setOrders(orders.filter(o => o.id !== id));
      toast.success('Order deleted successfully');
    } catch (error) {
      toast.error('Failed to delete order');
      console.error('Error deleting order:', error);
    }
  };

  const openEditDialog = (order) => {
    setEditingOrder(order);
    setFormData({
      party_name: order.party_name || '',
      bag_no: order.bag_no || '',
      order_images: order.order_images || [],
      cad_images: order.cad_images || [],
      stone_details_images: order.stone_details_images || [],
      remarks: order.remarks || '',
      confirmation_remark: order.confirmation_remark || '',
      status: order.status || 'On Process'
    });
    setIsEditDialogOpen(true);
  };

  const handleInlineRemarkUpdate = async (orderId, field, value) => {
    try {
      const updated = await cadOrderAPI.update(orderId, { [field]: value });
      setOrders(orders.map(o => o.id === orderId ? updated : o));
      toast.success('Remark updated');
    } catch (error) {
      toast.error('Failed to update remark');
    }
  };

  const downloadImage = (imageUrl, fileName) => {
    try {
      // For Base64 images, we need to convert and download differently
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = fileName || 'image.jpg';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Image downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const updated = await cadOrderAPI.update(orderId, { status: newStatus });
      setOrders(orders.map(o => o.id === orderId ? updated : o));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'On Process': 'default',
      'Doubt': 'destructive',
      'Ready': 'default'
    };
    return variants[status] || 'secondary';
  };

  // Full-screen image viewer functions
  const openFullScreenImage = (imageUrl, allImages = []) => {
    setFullScreenImage(imageUrl);
    setFullScreenImages(allImages.length > 0 ? allImages : [imageUrl]);
    setCurrentImageIndex(allImages.length > 0 ? allImages.indexOf(imageUrl) : 0);
    setImageZoom(1);
  };

  const closeFullScreenImage = () => {
    setFullScreenImage(null);
    setFullScreenImages([]);
    setCurrentImageIndex(0);
    setImageZoom(1);
  };

  const navigateImage = (direction) => {
    if (direction === 'next') {
      const nextIndex = (currentImageIndex + 1) % fullScreenImages.length;
      setCurrentImageIndex(nextIndex);
      setFullScreenImage(fullScreenImages[nextIndex]);
      setImageZoom(1);
    } else {
      const prevIndex = currentImageIndex === 0 ? fullScreenImages.length - 1 : currentImageIndex - 1;
      setCurrentImageIndex(prevIndex);
      setFullScreenImage(fullScreenImages[prevIndex]);
      setImageZoom(1);
    }
  };

  const handleZoom = (zoomType) => {
    if (zoomType === 'in' && imageZoom < 3) {
      setImageZoom(prev => prev + 0.25);
    } else if (zoomType === 'out' && imageZoom > 0.5) {
      setImageZoom(prev => prev - 0.25);
    }
  };

  // Filter orders based on search query
  const filterOrders = (ordersList) => {
    if (!searchQuery.trim()) return ordersList;
    
    const query = searchQuery.toLowerCase();
    return ordersList.filter(order => 
      (order.party_name?.toLowerCase().includes(query)) ||
      (order.bag_no?.toLowerCase().includes(query)) ||
      (order.remarks?.toLowerCase().includes(query)) ||
      (order.confirmation_remark?.toLowerCase().includes(query)) ||
      (order.status?.toLowerCase().includes(query))
    );
  };

  // Separate orders by status and apply search filter
  const allPendingOrders = orders.filter(o => o.status !== 'Ready');
  const allReadyOrders = orders.filter(o => o.status === 'Ready');
  
  const pendingOrders = filterOrders(allPendingOrders);
  const readyOrders = filterOrders(allReadyOrders);

  // Export to Excel
  const handleExport = () => {
    try {
      if (orders.length === 0) {
        toast.error('No orders to export');
        return;
      }

      const exportData = orders.map(order => ({
        'Party Name': order.party_name || '',
        'Bag No': order.bag_no || '',
        'Remarks': order.remarks || '',
        'Confirmation Remark': order.confirmation_remark || '',
        'Status': order.status || '',
        'Order Images': order.order_images?.length || 0,
        'CAD Images': order.cad_images?.length || 0,
        'Stone Images': order.stone_details_images?.length || 0,
        'Created At': order.created_at ? new Date(order.created_at).toLocaleString() : ''
      }));

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CAD Orders');
      
      // Generate filename with current date
      const fileName = `CAD_Orders_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      // Write file
      XLSX.writeFile(workbook, fileName);
      
      toast.success('Orders exported to Excel successfully');
    } catch (error) {
      toast.error('Failed to export orders');
      console.error('Export error:', error);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-lg bg-primary/10">
            <Layers className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">CAD Orders</h1>
            <p className="text-muted-foreground mt-1">Workflow from OPD to CAD Department</p>
          </div>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          {canAdd('cad') && (
            <DialogTrigger asChild>
              <Button className="shadow-emerald" onClick={resetForm}>
                <Plus className="h-4 w-4 mr-2" />
                New CAD Order
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create CAD Order</DialogTitle>
              <DialogDescription>Add a new order for CAD processing</DialogDescription>
            </DialogHeader>
            <OrderForm
              formData={formData}
              setFormData={setFormData}
              customers={customers}
              handleImageUpload={handleImageUpload}
              removeImage={removeImage}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAddOrder}>Create Order</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search and Export Controls */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by bag no, party name, remarks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={orders.length === 0}
              className="shadow-sm"
            >
              <FileDown className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pending/In Progress Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Active Orders ({pendingOrders.length})</CardTitle>
          <CardDescription>Orders in progress</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading orders...</p>
            </div>
          ) : pendingOrders.length === 0 ? (
            <div className="text-center py-12">
              <Layers className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No active orders</p>
              <p className="text-muted-foreground">All orders are ready or create a new one</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Party Name</TableHead>
                    <TableHead>Bag No</TableHead>
                    <TableHead>Image Preview</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead>Confirmation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingOrders.map((order) => (
                    <TableRow key={order.id}>
                      <TableCell className="font-medium">{order.party_name || '-'}</TableCell>
                      <TableCell>{order.bag_no || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap max-w-[200px]">
                          {order.order_images?.length > 0 && (
                            <div className="relative group">
                              <img 
                                src={order.order_images[0]} 
                                alt="Order" 
                                className="h-12 w-12 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => {
                                  const allImages = [
                                    ...(order.order_images || []),
                                    ...(order.cad_images || []),
                                    ...(order.stone_details_images || [])
                                  ];
                                  openFullScreenImage(order.order_images[0], allImages);
                                }}
                              />
                              <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(order.order_images[0], `Order_${order.bag_no || 'image'}_1.jpg`);
                                }}
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </button>
                            </div>
                          )}
                          {order.cad_images?.length > 0 && (
                            <div className="relative group">
                              <img 
                                src={order.cad_images[0]} 
                                alt="CAD" 
                                className="h-12 w-12 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => {
                                  const allImages = [
                                    ...(order.order_images || []),
                                    ...(order.cad_images || []),
                                    ...(order.stone_details_images || [])
                                  ];
                                  openFullScreenImage(order.cad_images[0], allImages);
                                }}
                              />
                              <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(order.cad_images[0], `CAD_${order.bag_no || 'image'}_1.jpg`);
                                }}
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </button>
                            </div>
                          )}
                          {order.stone_details_images?.length > 0 && (
                            <div className="relative group">
                              <img 
                                src={order.stone_details_images[0]} 
                                alt="Stone" 
                                className="h-12 w-12 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => {
                                  const allImages = [
                                    ...(order.order_images || []),
                                    ...(order.cad_images || []),
                                    ...(order.stone_details_images || [])
                                  ];
                                  openFullScreenImage(order.stone_details_images[0], allImages);
                                }}
                              />
                              <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(order.stone_details_images[0], `Stone_${order.bag_no || 'image'}_1.jpg`);
                                }}
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </button>
                            </div>
                          )}
                          {!order.order_images?.length && !order.cad_images?.length && !order.stone_details_images?.length && (
                            <span className="text-xs text-muted-foreground">No images</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          defaultValue={order.remarks || ''}
                          placeholder="Add remarks..."
                          className="w-48 text-sm"
                          onBlur={(e) => {
                            if (e.target.value !== order.remarks) {
                              handleInlineRemarkUpdate(order.id, 'remarks', e.target.value);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          defaultValue={order.confirmation_remark || ''}
                          placeholder="Add confirmation..."
                          className="w-48 text-sm"
                          onBlur={(e) => {
                            if (e.target.value !== order.confirmation_remark) {
                              handleInlineRemarkUpdate(order.id, 'confirmation_remark', e.target.value);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={order.status}
                          onValueChange={(value) => handleStatusChange(order.id, value)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="On Process">On Process</SelectItem>
                            <SelectItem value="Doubt">Doubt</SelectItem>
                            <SelectItem value="Ready">Ready</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setViewingOrder(order);
                              setIsViewDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEdit('cad') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(order)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete('cad') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteOrder(order.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ready Orders Section */}
      {readyOrders.length > 0 && (
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <CardTitle className="text-green-700">Ready Orders ({readyOrders.length})</CardTitle>
            <CardDescription>Orders completed and ready for delivery</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Party Name</TableHead>
                    <TableHead>Bag No</TableHead>
                    <TableHead>Image Preview</TableHead>
                    <TableHead>Remarks</TableHead>
                    <TableHead>Confirmation</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {readyOrders.map((order) => (
                    <TableRow key={order.id} className="bg-white">
                      <TableCell className="font-medium">{order.party_name || '-'}</TableCell>
                      <TableCell>{order.bag_no || '-'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap max-w-[200px]">
                          {order.order_images?.length > 0 && (
                            <div className="relative group">
                              <img 
                                src={order.order_images[0]} 
                                alt="Order" 
                                className="h-12 w-12 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => {
                                  const allImages = [
                                    ...(order.order_images || []),
                                    ...(order.cad_images || []),
                                    ...(order.stone_details_images || [])
                                  ];
                                  openFullScreenImage(order.order_images[0], allImages);
                                }}
                              />
                              <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(order.order_images[0], `Order_${order.bag_no || 'image'}_1.jpg`);
                                }}
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </button>
                            </div>
                          )}
                          {order.cad_images?.length > 0 && (
                            <div className="relative group">
                              <img 
                                src={order.cad_images[0]} 
                                alt="CAD" 
                                className="h-12 w-12 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => {
                                  const allImages = [
                                    ...(order.order_images || []),
                                    ...(order.cad_images || []),
                                    ...(order.stone_details_images || [])
                                  ];
                                  openFullScreenImage(order.cad_images[0], allImages);
                                }}
                              />
                              <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(order.cad_images[0], `CAD_${order.bag_no || 'image'}_1.jpg`);
                                }}
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </button>
                            </div>
                          )}
                          {order.stone_details_images?.length > 0 && (
                            <div className="relative group">
                              <img 
                                src={order.stone_details_images[0]} 
                                alt="Stone" 
                                className="h-12 w-12 object-cover rounded border cursor-pointer hover:opacity-80"
                                onClick={() => {
                                  const allImages = [
                                    ...(order.order_images || []),
                                    ...(order.cad_images || []),
                                    ...(order.stone_details_images || [])
                                  ];
                                  openFullScreenImage(order.stone_details_images[0], allImages);
                                }}
                              />
                              <button
                                className="absolute -top-1 -right-1 bg-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  downloadImage(order.stone_details_images[0], `Stone_${order.bag_no || 'image'}_1.jpg`);
                                }}
                              >
                                <Download className="h-3 w-3 text-primary" />
                              </button>
                            </div>
                          )}
                          {!order.order_images?.length && !order.cad_images?.length && !order.stone_details_images?.length && (
                            <span className="text-xs text-muted-foreground">No images</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Input
                          defaultValue={order.remarks || ''}
                          placeholder="Add remarks..."
                          className="w-48 text-sm"
                          onBlur={(e) => {
                            if (e.target.value !== order.remarks) {
                              handleInlineRemarkUpdate(order.id, 'remarks', e.target.value);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          defaultValue={order.confirmation_remark || ''}
                          placeholder="Add confirmation..."
                          className="w-48 text-sm"
                          onBlur={(e) => {
                            if (e.target.value !== order.confirmation_remark) {
                              handleInlineRemarkUpdate(order.id, 'confirmation_remark', e.target.value);
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={order.status}
                          onValueChange={(value) => handleStatusChange(order.id, value)}
                        >
                          <SelectTrigger className="w-[130px]">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="On Process">On Process</SelectItem>
                            <SelectItem value="Doubt">Doubt</SelectItem>
                            <SelectItem value="Ready">Ready</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setViewingOrder(order);
                              setIsViewDialogOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canEdit('cad') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(order)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete('cad') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteOrder(order.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit CAD Order</DialogTitle>
            <DialogDescription>Update order details</DialogDescription>
          </DialogHeader>
          <OrderForm
            formData={formData}
            setFormData={setFormData}
            customers={customers}
            handleImageUpload={handleImageUpload}
            removeImage={removeImage}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditOrder}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>CAD Order Details</DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Party Name</Label>
                  <p className="font-medium">{viewingOrder.party_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Bag No</Label>
                  <p className="font-medium">{viewingOrder.bag_no || '-'}</p>
                </div>
              </div>
              
              <ImageGallery
                title="Order Images"
                images={viewingOrder.order_images || []}
                onDownload={downloadImage}
                onImageClick={(img) => {
                  const allImages = [
                    ...(viewingOrder.order_images || []),
                    ...(viewingOrder.cad_images || []),
                    ...(viewingOrder.stone_details_images || [])
                  ];
                  openFullScreenImage(img, allImages);
                }}
              />
              
              <ImageGallery
                title="CAD Images"
                images={viewingOrder.cad_images || []}
                onDownload={downloadImage}
                onImageClick={(img) => {
                  const allImages = [
                    ...(viewingOrder.order_images || []),
                    ...(viewingOrder.cad_images || []),
                    ...(viewingOrder.stone_details_images || [])
                  ];
                  openFullScreenImage(img, allImages);
                }}
              />
              
              <ImageGallery
                title="Stone Details Images"
                images={viewingOrder.stone_details_images || []}
                onDownload={downloadImage}
                onImageClick={(img) => {
                  const allImages = [
                    ...(viewingOrder.order_images || []),
                    ...(viewingOrder.cad_images || []),
                    ...(viewingOrder.stone_details_images || [])
                  ];
                  openFullScreenImage(img, allImages);
                }}
              />
              
              <div>
                <Label className="text-muted-foreground">Remarks</Label>
                <p className="font-medium mt-1">{viewingOrder.remarks || '-'}</p>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Confirmation Remark</Label>
                <p className="font-medium mt-1">{viewingOrder.confirmation_remark || '-'}</p>
              </div>
              
              <div>
                <Label className="text-muted-foreground">Status</Label>
                <div className="mt-1">
                  <Badge variant={getStatusBadge(viewingOrder.status)}>
                    {viewingOrder.status}
                  </Badge>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-Screen Image Viewer */}
      {fullScreenImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeFullScreenImage}
        >
          <div className="relative w-full h-full flex items-center justify-center p-4">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
              onClick={(e) => {
                e.stopPropagation();
                closeFullScreenImage();
              }}
            >
              <X className="h-6 w-6" />
            </Button>

            {/* Zoom Controls */}
            <div className="absolute top-4 left-4 flex gap-2 z-10">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoom('in');
                }}
                disabled={imageZoom >= 3}
              >
                <ZoomIn className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={(e) => {
                  e.stopPropagation();
                  handleZoom('out');
                }}
                disabled={imageZoom <= 0.5}
              >
                <ZoomOut className="h-5 w-5" />
              </Button>
              <span className="text-white text-sm px-3 py-2 bg-black/50 rounded">
                {Math.round(imageZoom * 100)}%
              </span>
            </div>

            {/* Download Button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute bottom-4 right-4 text-white hover:bg-white/20 z-10"
              onClick={(e) => {
                e.stopPropagation();
                downloadImage(fullScreenImage, `Image_${currentImageIndex + 1}.jpg`);
              }}
            >
              <Download className="h-6 w-6" />
            </Button>

            {/* Navigation Buttons */}
            {fullScreenImages.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('prev');
                  }}
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigateImage('next');
                  }}
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded">
                  {currentImageIndex + 1} / {fullScreenImages.length}
                </div>
              </>
            )}

            {/* Image */}
            <img
              src={fullScreenImage}
              alt="Full screen view"
              className="max-w-full max-h-full object-contain transition-transform duration-200"
              style={{ transform: `scale(${imageZoom})` }}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Order Form Component
const OrderForm = ({ formData, setFormData, customers, handleImageUpload, removeImage }) => {
  return (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Party Name</Label>
          <Select
            value={formData.party_name}
            onValueChange={(value) => setFormData({ ...formData, party_name: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select party" />
            </SelectTrigger>
            <SelectContent>
              {customers.filter(c => c.name && c.name.trim() !== '').map(customer => (
                <SelectItem key={customer.id} value={customer.name}>
                  {customer.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Bag No</Label>
          <Input
            value={formData.bag_no}
            onChange={(e) => setFormData({ ...formData, bag_no: e.target.value })}
            placeholder="Enter bag number"
          />
        </div>
      </div>

      <ImageUploadSection
        title="Order Images (Up to 3)"
        images={formData.order_images}
        onUpload={(e) => handleImageUpload(e, 'order_images')}
        onRemove={(index) => removeImage('order_images', index)}
      />

      <ImageUploadSection
        title="CAD Images (Up to 3)"
        images={formData.cad_images}
        onUpload={(e) => handleImageUpload(e, 'cad_images')}
        onRemove={(index) => removeImage('cad_images', index)}
      />

      <ImageUploadSection
        title="Stone Details Images (Up to 3)"
        images={formData.stone_details_images}
        onUpload={(e) => handleImageUpload(e, 'stone_details_images')}
        onRemove={(index) => removeImage('stone_details_images', index)}
      />

      <div className="space-y-2">
        <Label>Remarks</Label>
        <Textarea
          value={formData.remarks}
          onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
          placeholder="Add remarks (optional)"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>Confirmation Remark</Label>
        <Textarea
          value={formData.confirmation_remark}
          onChange={(e) => setFormData({ ...formData, confirmation_remark: e.target.value })}
          placeholder="Add confirmation remark (optional)"
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value) => setFormData({ ...formData, status: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="On Process">On Process</SelectItem>
            <SelectItem value="Doubt">Doubt</SelectItem>
            <SelectItem value="Ready">Ready</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

// Image Upload Section Component
const ImageUploadSection = ({ title, images, onUpload, onRemove }) => {
  return (
    <div className="space-y-2">
      <Label>{title}</Label>
      <Input
        type="file"
        accept="image/*"
        multiple
        onChange={onUpload}
        className="cursor-pointer"
        disabled={images.length >= 3}
      />
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {images.map((img, index) => (
            <div key={index} className="relative">
              <img
                src={img}
                alt={`Upload ${index + 1}`}
                className="h-24 w-24 object-cover rounded-md border"
              />
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                onClick={() => onRemove(index)}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        {images.length}/3 images uploaded. Max 5MB per image.
      </p>
    </div>
  );
};

// Image Gallery Component
const ImageGallery = ({ title, images, onDownload, onImageClick }) => {
  if (!images || images.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label className="text-muted-foreground">{title}</Label>
      <div className="flex flex-wrap gap-3">
        {images.map((img, index) => (
          <div key={index} className="relative group">
            <img
              src={img}
              alt={`${title} ${index + 1}`}
              className="h-40 w-40 object-cover rounded-md border cursor-pointer hover:opacity-80"
              onClick={() => onImageClick && onImageClick(img)}
            />
            <Button
              size="sm"
              variant="secondary"
              className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(img, `${title.replace(/\s+/g, '_')}_${index + 1}.jpg`);
              }}
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">Click image for full-screen view, hover for download button</p>
    </div>
  );
};

export default CADOrders;
